import { areImagesEqual } from '../drawing.utils';
import { getContextRef } from '../drawing.refs';
import type { DrawingHistoryState } from '../drawing.types';
import type { DrawingStateCreator, HistorySlice } from './types';

export const createHistorySlice: DrawingStateCreator<HistorySlice> = (
  set,
  get
) => ({
  history: {
    states: [],
    currentIndex: -1,
    maxStates: 50,
    canUndo: false,
    canRedo: false,
    lastSaveIndex: -1,
  },

  undo: () => {
    const { history } = get();

    const contextRef = getContextRef();
    if (!contextRef || history.currentIndex <= 0) return;

    const newIndex = history.currentIndex - 1;
    const state = history.states[newIndex];

    if (state) {
      contextRef.putImageData(state.imageData, 0, 0);
      set((draft) => {
        draft.history.currentIndex = newIndex;
        draft.history.canUndo = newIndex > 0;
        draft.history.canRedo = true;
      });

      get().checkForChanges();
    }
  },

  redo: () => {
    const { history } = get();

    const contextRef = getContextRef();
    if (!contextRef || history.currentIndex >= history.states.length - 1)
      return;

    const newIndex = history.currentIndex + 1;
    const state = history.states[newIndex];

    if (state) {
      contextRef.putImageData(state.imageData, 0, 0);
      set((draft) => {
        draft.history.currentIndex = newIndex;
        draft.history.canUndo = true;
        draft.history.canRedo = newIndex < history.states.length - 1;
      });

      get().checkForChanges();
    }
  },

  saveToHistory: (action = 'Draw') => {
    const { canvas } = get();
    const { width, height } = canvas;

    const contextRef = getContextRef();
    if (!contextRef) return;

    const imageData = contextRef.getImageData(0, 0, width, height);
    const newState: DrawingHistoryState = {
      imageData,
      timestamp: Date.now(),
      action,
    };

    set((state) => {
      // Remove states after current index
      state.history.states = state.history.states.slice(
        0,
        state.history.currentIndex + 1
      );

      // Add new state
      state.history.states.push(newState);

      // Limit history size
      if (state.history.states.length > state.history.maxStates) {
        state.history.states.shift();
        state.history.currentIndex = state.history.states.length - 1;
      } else {
        state.history.currentIndex++;
      }

      state.history.canUndo = state.history.currentIndex > 0;
      state.history.canRedo = false;
    });
  },

  clearHistory: () =>
    set((state) => {
      state.history.states = [];
      state.history.currentIndex = -1;
      state.history.canUndo = false;
      state.history.canRedo = false;
    }),

  checkForChanges: () => {
    const { canvas } = get();
    const { width, height, initialImageData } = canvas;

    const contextRef = getContextRef();
    if (!contextRef || !initialImageData) return false;

    const currentImageData = contextRef.getImageData(0, 0, width, height);
    const hasChanges = !areImagesEqual(currentImageData, initialImageData);

    set((state) => {
      state.canvas.hasChanges = hasChanges;
    });

    return hasChanges;
  },
});
